import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CalendarDays, Plus, Check, Trash2, ChevronLeft, ChevronRight, Clock, AlertTriangle, RotateCcw, X, Loader2, Pencil, Smartphone, Copy, RefreshCw, Globe, Send } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'restaurant', label: 'Restaurant', color: '#C9A84C' },
  { id: 'traiteur', label: 'Traiteur', color: '#0d9488' },
  { id: 'admin', label: 'Administration', color: '#6B7280' },
  { id: 'fournisseurs', label: 'Fournisseurs', color: '#E07A5F' },
  { id: 'rh', label: 'RH / Equipe', color: '#5B8FE0' },
  { id: 'commercial', label: 'Commercial', color: '#8B5CF6' },
  { id: 'perso', label: 'Personnel', color: '#F59E0B' },
];

const PRIORITIES = [
  { id: 'haute', label: 'Haute', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  { id: 'moyenne', label: 'Moyenne', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  { id: 'basse', label: 'Basse', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
];

const RECURRING = [
  { id: 'none', label: 'Aucune' },
  { id: 'daily', label: 'Quotidien' },
  { id: 'weekly', label: 'Hebdomadaire' },
  { id: 'monthly', label: 'Mensuel' },
];

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const catColor = (id) => CATEGORIES.find(c => c.id === id)?.color || '#6B7280';
const priColor = (id) => PRIORITIES.find(p => p.id === id) || PRIORITIES[1];

export default function TaskManager() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [view, setView] = useState('calendar');
  const [showIcal, setShowIcal] = useState(false);
  const [quickText, setQuickText] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);
  const [externalEvents, setExternalEvents] = useState([]);
  const [showCalendars, setShowCalendars] = useState(false);

  const month = currentDate.getFullYear() + '-' + String(currentDate.getMonth() + 1).padStart(2, '0');

  const loadTasks = useCallback(async () => {
    try {
      const res = await api.get(`/tasks?month=${month}`);
      setTasks(res.data);
    } catch { setTasks([]); }
    setLoading(false);
  }, [month]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Load external events for the current month
  useEffect(() => {
    api.get(`/tasks/external-events?month=${month}`)
      .then(r => setExternalEvents(r.data || []))
      .catch(() => setExternalEvents([]));
  }, [month]);

  const toggleTask = async (id) => {
    try {
      const res = await api.post(`/tasks/${id}/toggle`);
      setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: res.data.completed } : t));
      if (res.data.completed) toast.success('Tache terminee');
    } catch { toast.error('Erreur'); }
  };

  const quickAdd = async () => {
    if (!quickText.trim()) return;
    setQuickSaving(true);
    try {
      const res = await api.post('/tasks', {
        title: quickText.trim(),
        date: new Date().toISOString().split('T')[0],
        priority: 'moyenne',
        category: 'restaurant',
        recurring: 'none',
      });
      setTasks(prev => [...prev, res.data]);
      setQuickText('');
      toast.success('Tache ajoutee');
    } catch { toast.error('Erreur'); }
    setQuickSaving(false);
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks(prev => prev.filter(t => t.id !== id));
      toast.success('Tache supprimee');
    } catch { toast.error('Erreur'); }
  };

  const openNew = (date) => {
    setEditTask(null);
    setSelectedDate(date || new Date().toISOString().split('T')[0]);
    setShowModal(true);
  };

  const openEdit = (task) => {
    setEditTask(task);
    setSelectedDate(task.date);
    setShowModal(true);
  };

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  // Calendar grid
  const year = currentDate.getFullYear();
  const monthIdx = currentDate.getMonth();
  const firstDay = new Date(year, monthIdx, 1);
  const lastDay = new Date(year, monthIdx + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7; // Monday=0
  const daysInMonth = lastDay.getDate();

  const todayStr = new Date().toISOString().split('T')[0];
  const filtered = filterCat === 'all' ? tasks : tasks.filter(t => t.category === filterCat);

  // Tasks grouped by date
  const tasksByDate = {};
  filtered.forEach(t => {
    if (!tasksByDate[t.date]) tasksByDate[t.date] = [];
    tasksByDate[t.date].push(t);
  });

  // External events grouped by date
  const eventsByDate = {};
  externalEvents.forEach(e => {
    if (!eventsByDate[e.date]) eventsByDate[e.date] = [];
    eventsByDate[e.date].push(e);
  });

  // Priority stats
  const pending = filtered.filter(t => !t.completed);
  const haute = pending.filter(t => t.priority === 'haute').length;
  const todayTasks = pending.filter(t => t.date === todayStr);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4" data-testid="task-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarDays size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Taches & Planning</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 p-0.5 bg-[rgb(var(--lm-card))] rounded-lg">
            <button onClick={() => setView('calendar')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${view === 'calendar' ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))]'}`}
              data-testid="view-calendar">Calendrier</button>
            <button onClick={() => setView('list')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${view === 'list' ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))]'}`}
              data-testid="view-list">Liste</button>
          </div>
          <Button size="sm" onClick={() => openNew()} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-8" data-testid="add-task-btn">
            <Plus size={14} className="mr-1" /> Nouvelle tache
          </Button>
          <button onClick={() => setShowCalendars(true)} className="p-1.5 rounded-md border border-[rgb(var(--lm-border))] hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] transition-colors" title="Calendriers externes" data-testid="external-cal-btn">
            <Globe size={15} />
          </button>
          <button onClick={() => setShowIcal(true)} className="p-1.5 rounded-md border border-[rgb(var(--lm-border))] hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] transition-colors" title="Synchroniser iPhone" data-testid="ical-btn">
            <Smartphone size={15} />
          </button>
        </div>
      </div>

      {/* Quick add */}
      <div className="lm-card p-2.5 flex items-center gap-2" data-testid="quick-add">
        <div className="w-5 h-5 rounded-full border-2 border-dashed border-[rgb(var(--lm-gold)/0.4)] flex items-center justify-center shrink-0">
          <Plus size={10} className="text-[rgb(var(--lm-gold))]" />
        </div>
        <input
          value={quickText}
          onChange={e => setQuickText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && quickAdd()}
          placeholder="Ajout rapide — tapez et Entree..."
          className="flex-1 bg-transparent text-xs text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted)/0.5)] outline-none"
          data-testid="quick-add-input"
        />
        {quickText.trim() && (
          <button onClick={quickAdd} disabled={quickSaving}
            className="px-2.5 py-1 rounded-md bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] text-[10px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="quick-add-submit">
            {quickSaving ? <Loader2 size={10} className="animate-spin" /> : 'Ajouter'}
          </button>
        )}
      </div>

      {/* Overdue banner */}
      {(() => {
        const overdue = pending.filter(t => t.date < todayStr);
        if (overdue.length === 0) return null;
        return (
          <div className="p-2.5 rounded-lg bg-red-500/8 border border-red-500/15 flex items-center gap-2" data-testid="overdue-banner">
            <AlertTriangle size={14} className="text-red-400 shrink-0" />
            <span className="text-xs text-red-400 font-medium">{overdue.length} tache{overdue.length > 1 ? 's' : ''} en retard</span>
            <span className="text-[10px] text-red-400/60">— rappels Telegram actifs tant que non terminees</span>
          </div>
        );
      })()}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="lm-card p-3 text-center">
          <p className="text-2xl font-light text-[rgb(var(--lm-text))]" data-testid="stat-today">{todayTasks.length}</p>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">Aujourd'hui</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-2xl font-light text-red-400" data-testid="stat-haute">{haute}</p>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">Priorite haute</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-2xl font-light text-orange-400" data-testid="stat-overdue">{pending.filter(t => t.date < todayStr).length}</p>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">En retard</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-2xl font-light text-[rgb(var(--lm-text))]" data-testid="stat-pending">{pending.length}</p>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">En cours</p>
        </div>
        <div className="lm-card p-3 text-center">
          <p className="text-2xl font-light text-[rgb(var(--lm-green))]" data-testid="stat-done">{filtered.length - pending.length}</p>
          <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))]">Terminees</p>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => setFilterCat('all')}
          className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all border ${filterCat === 'all' ? 'border-[rgb(var(--lm-gold))] text-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-gold)/0.08)]' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))]'}`}
          data-testid="filter-all">Toutes</button>
        {CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setFilterCat(cat.id)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all border ${filterCat === cat.id ? 'bg-opacity-15' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))]'}`}
            style={filterCat === cat.id ? { borderColor: cat.color, color: cat.color, backgroundColor: cat.color + '15' } : {}}
            data-testid={`filter-${cat.id}`}>{cat.label}</button>
        ))}
      </div>

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="lm-card p-4" data-testid="calendar-view">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="prev-month">
              <ChevronLeft size={16} />
            </button>
            <div className="text-center">
              <h2 className="text-base font-medium text-[rgb(var(--lm-text))]">{MONTHS_FR[monthIdx]} {year}</h2>
              <button onClick={goToday} className="text-[9px] text-[rgb(var(--lm-gold))] hover:underline" data-testid="go-today">Aujourd'hui</button>
            </div>
            <button onClick={nextMonth} className="p-1.5 rounded-md hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid="next-month">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS_FR.map(d => (
              <div key={d} className="text-center text-[9px] font-medium text-[rgb(var(--lm-text-muted))] uppercase tracking-wider py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px bg-[rgb(var(--lm-border)/0.3)] rounded-lg overflow-hidden">
            {/* Empty cells before first day */}
            {Array.from({ length: startDow }).map((_, i) => (
              <div key={`empty-${i}`} className="bg-[rgb(var(--lm-bg))] min-h-[80px] p-1 opacity-30" />
            ))}
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayTasks = tasksByDate[dateStr] || [];
              const dayEvents = eventsByDate[dateStr] || [];
              const totalItems = dayTasks.length + dayEvents.length;
              const isToday = dateStr === todayStr;
              const isWeekend = new Date(dateStr).getDay() === 0 || new Date(dateStr).getDay() === 6;

              return (
                <div key={day}
                  onClick={() => openNew(dateStr)}
                  className={`min-h-[80px] p-1 cursor-pointer transition-colors hover:bg-[rgb(var(--lm-surface-elevated))] ${
                    isToday ? 'bg-[rgb(var(--lm-gold)/0.06)]' : isWeekend ? 'bg-[rgb(var(--lm-surface)/0.5)]' : 'bg-[rgb(var(--lm-bg))]'
                  }`}
                  data-testid={`cal-day-${day}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={`text-xs font-medium w-5 h-5 flex items-center justify-center rounded-full ${
                      isToday ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]' : 'text-[rgb(var(--lm-text-secondary))]'
                    }`}>{day}</span>
                    {totalItems > 0 && (
                      <span className="text-[8px] text-[rgb(var(--lm-text-muted))]">{totalItems}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 2).map(ev => (
                      <div key={ev.id}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[9px] leading-tight px-1 py-0.5 rounded truncate"
                        style={{ backgroundColor: (ev.color || '#3B82F6') + '18', color: ev.color || '#3B82F6', borderLeft: `2px solid ${ev.color || '#3B82F6'}` }}>
                        {ev.time ? `${ev.time} ` : ''}{ev.title}
                      </div>
                    ))}
                    {dayTasks.slice(0, 3 - Math.min(dayEvents.length, 2)).map(t => (
                      <div key={t.id}
                        onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                        className={`text-[9px] leading-tight px-1 py-0.5 rounded truncate cursor-pointer transition-opacity ${t.completed ? 'opacity-40 line-through' : 'hover:opacity-80'}`}
                        style={{ backgroundColor: catColor(t.category) + '18', color: catColor(t.category), borderLeft: `2px solid ${priColor(t.priority).color}` }}
                        data-testid={`cal-task-${t.id}`}>
                        {t.time ? `${t.time} ` : ''}{t.title}
                      </div>
                    ))}
                    {totalItems > 3 && (
                      <div className="text-[8px] text-[rgb(var(--lm-text-muted))] text-center">+{totalItems - 3}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="space-y-1" data-testid="list-view">
          {pending.length === 0 && (
            <div className="lm-card p-8 text-center">
              <CalendarDays size={32} className="mx-auto text-[rgb(var(--lm-text-muted)/0.3)] mb-2" />
              <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune tache en cours</p>
            </div>
          )}
          {/* Group by date */}
          {Object.entries(tasksByDate)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, dateTasks]) => {
              const dt = new Date(date + 'T00:00:00');
              const dayLabel = dt.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
              const pendingDay = dateTasks.filter(t => !t.completed);
              const isToday = date === todayStr;
              const isPast = date < todayStr;

              return (
                <div key={date} className="mb-3">
                  <div className={`flex items-center gap-2 mb-1 px-1 ${isToday ? 'text-[rgb(var(--lm-gold))]' : isPast ? 'text-[rgb(var(--lm-text-muted))]' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                    <span className="text-[10px] font-medium uppercase tracking-wider">{dayLabel}</span>
                    {isToday && <span className="text-[8px] px-1.5 py-0.5 bg-[rgb(var(--lm-gold)/0.15)] rounded-full font-bold">Aujourd'hui</span>}
                    {isPast && pendingDay.length > 0 && <AlertTriangle size={10} className="text-red-400" />}
                  </div>
                  <div className="space-y-1">
                    {dateTasks.map(t => (
                      <TaskRow key={t.id} task={t} onToggle={toggleTask} onEdit={openEdit} onDelete={deleteTask} />
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <TaskModal
          date={selectedDate}
          task={editTask}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadTasks(); }}
        />
      )}

      {/* iCal Sync Modal */}
      {showIcal && <ICalModal onClose={() => setShowIcal(false)} />}

      {/* External Calendars Modal */}
      {showCalendars && <ExternalCalendarsModal onClose={() => setShowCalendars(false)} onSynced={() => {
        api.get(`/tasks/external-events?month=${month}`).then(r => setExternalEvents(r.data || [])).catch(() => {});
      }} />}
    </div>
  );
}

/* ── Task Row ── */
function TaskRow({ task, onToggle, onEdit, onDelete }) {
  const pri = priColor(task.priority);
  const cat = CATEGORIES.find(c => c.id === task.category);

  return (
    <div className={`lm-card p-2.5 flex items-center gap-2.5 group transition-all ${task.completed ? 'opacity-50' : ''}`}
      data-testid={`task-row-${task.id}`}>
      <button onClick={() => onToggle(task.id)}
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
          task.completed ? 'border-[rgb(var(--lm-green))] bg-[rgb(var(--lm-green))]' : 'border-[rgb(var(--lm-border))] hover:border-[rgb(var(--lm-green))]'
        }`}
        data-testid={`toggle-${task.id}`}>
        {task.completed && <Check size={10} className="text-white" />}
      </button>
      <div className="w-1 h-8 rounded-full shrink-0" style={{ backgroundColor: pri.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-medium truncate ${task.completed ? 'line-through text-[rgb(var(--lm-text-muted))]' : 'text-[rgb(var(--lm-text))]'}`}>{task.title}</span>
          {task.recurring !== 'none' && <RotateCcw size={9} className="text-[rgb(var(--lm-text-muted))] shrink-0" />}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {task.time && <span className="text-[9px] text-[rgb(var(--lm-text-muted))] flex items-center gap-0.5"><Clock size={8} />{task.time}</span>}
          {cat && <span className="text-[8px] px-1 py-0.5 rounded" style={{ backgroundColor: cat.color + '18', color: cat.color }}>{cat.label}</span>}
          <span className="text-[8px] px-1 py-0.5 rounded" style={{ backgroundColor: pri.bg, color: pri.color }}>{pri.id === 'haute' ? 'HAUTE' : pri.id === 'basse' ? 'Basse' : 'Moy.'}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onEdit(task)} className="p-1 rounded hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))]" data-testid={`edit-${task.id}`}>
          <Pencil size={11} />
        </button>
        <button onClick={() => onDelete(task.id)} className="p-1 rounded hover:bg-[rgb(var(--lm-surface-elevated))] text-red-400" data-testid={`delete-${task.id}`}>
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

/* ── Task Modal ── */
function TaskModal({ date, task, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    date: task?.date || date || '',
    time: task?.time || '',
    priority: task?.priority || 'moyenne',
    category: task?.category || 'restaurant',
    recurring: task?.recurring || 'none',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      if (task) {
        await api.put(`/tasks/${task.id}`, form);
        toast.success('Tache modifiee');
      } else {
        await api.post('/tasks', form);
        toast.success('Tache creee');
      }
      onSaved();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="lm-card w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="task-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-[rgb(var(--lm-text))]">{task ? 'Modifier' : 'Nouvelle tache'}</h2>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
        </div>

        <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Titre de la tache"
          className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-sm" autoFocus data-testid="task-title" />

        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Description (optionnel)" rows={2}
          className="w-full bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-3 py-2 text-xs text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] resize-none focus:outline-none focus:ring-1 focus:ring-[rgb(var(--lm-gold))]"
          data-testid="task-description" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1 block">Date</label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="task-date" />
          </div>
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1 block">Heure</label>
            <Input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
              className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="task-time" />
          </div>
        </div>

        {/* Priority */}
        <div>
          <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1.5 block">Priorite</label>
          <div className="flex gap-1.5">
            {PRIORITIES.map(p => (
              <button key={p.id} onClick={() => setForm(f => ({ ...f, priority: p.id }))}
                className={`flex-1 py-1.5 rounded-md text-[10px] font-medium transition-all border ${
                  form.priority === p.id ? 'border-opacity-100' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))]'
                }`}
                style={form.priority === p.id ? { borderColor: p.color, color: p.color, backgroundColor: p.bg } : {}}
                data-testid={`priority-${p.id}`}>{p.label}</button>
            ))}
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1.5 block">Categorie</label>
          <div className="flex gap-1 flex-wrap">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setForm(f => ({ ...f, category: c.id }))}
                className={`px-2 py-1 rounded-md text-[9px] font-medium transition-all border ${
                  form.category === c.id ? '' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))]'
                }`}
                style={form.category === c.id ? { borderColor: c.color, color: c.color, backgroundColor: c.color + '15' } : {}}
                data-testid={`category-${c.id}`}>{c.label}</button>
            ))}
          </div>
        </div>

        {/* Recurring */}
        <div>
          <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-1.5 block">Recurrence</label>
          <div className="flex gap-1.5">
            {RECURRING.map(r => (
              <button key={r.id} onClick={() => setForm(f => ({ ...f, recurring: r.id }))}
                className={`flex-1 py-1.5 rounded-md text-[10px] font-medium transition-all border ${
                  form.recurring === r.id ? 'border-[rgb(var(--lm-gold))] text-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-gold)/0.08)]' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))]'
                }`}
                data-testid={`recurring-${r.id}`}>{r.label}</button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="flex-1 text-xs h-9">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-9" data-testid="save-task">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            {task ? 'Modifier' : 'Creer'}
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ── iCal Sync Modal ── */
function ICalModal({ onClose }) {
  const icalUrl = `${process.env.REACT_APP_BACKEND_URL}/api/tasks/calendar.ics`;
  const [copied, setCopied] = useState(false);

  const copyUrl = () => {
    navigator.clipboard.writeText(icalUrl).then(() => {
      setCopied(true);
      toast.success('Lien copie !');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="lm-card w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="ical-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone size={16} className="text-[rgb(var(--lm-gold))]" />
            <h2 className="text-sm font-medium text-[rgb(var(--lm-text))]">Synchroniser avec iPhone</h2>
          </div>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
        </div>

        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider mb-2">Lien du calendrier</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[10px] text-[rgb(var(--lm-text))] bg-[rgb(var(--lm-bg))] px-2 py-1.5 rounded overflow-hidden text-ellipsis whitespace-nowrap" data-testid="ical-url">
                {icalUrl}
              </code>
              <button onClick={copyUrl}
                className={`p-1.5 rounded-md border transition-all ${copied ? 'border-[rgb(var(--lm-green))] text-[rgb(var(--lm-green))]' : 'border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`}
                data-testid="copy-ical-url">
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Comment faire sur iPhone</p>
            <div className="space-y-1.5">
              {[
                { step: '1', text: 'Copiez le lien ci-dessus' },
                { step: '2', text: 'Reglages > Calendrier > Comptes' },
                { step: '3', text: 'Ajouter un compte > Autre' },
                { step: '4', text: 'S\'abonner a un calendrier' },
                { step: '5', text: 'Collez le lien et validez' },
              ].map(s => (
                <div key={s.step} className="flex items-start gap-2">
                  <span className="w-4 h-4 rounded-full bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))] text-[9px] font-bold flex items-center justify-center shrink-0 mt-0.5">{s.step}</span>
                  <span className="text-xs text-[rgb(var(--lm-text-secondary))]">{s.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-2.5 rounded-lg bg-[rgb(var(--lm-gold)/0.06)] border border-[rgb(var(--lm-gold)/0.15)]">
            <p className="text-[10px] text-[rgb(var(--lm-gold))]">
              Vos taches se synchronisent automatiquement. Les alertes (1h avant pour haute priorite, 30min pour les autres) s'afficheront aussi sur votre iPhone.
            </p>
          </div>
        </div>

        <Button onClick={copyUrl} className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-9" data-testid="copy-ical-btn">
          <Copy size={13} className="mr-1.5" /> {copied ? 'Copie !' : 'Copier le lien'}
        </Button>
      </div>
    </div>
  );
}


/* ── External Calendars Modal ── */
function ExternalCalendarsModal({ onClose, onSynced }) {
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = useCallback(() => {
    api.get('/tasks/external-calendars').then(r => setCalendars(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addCal = async () => {
    if (!url.trim()) { toast.error('URL requise'); return; }
    setAdding(true);
    try {
      await api.post('/tasks/external-calendars', { url: url.trim(), name: name.trim() || 'Mon calendrier', color: '#3B82F6' });
      toast.success('Calendrier ajoute !');
      setUrl('');
      setName('');
      load();
      syncAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setAdding(false);
  };

  const removeCal = async (id) => {
    try {
      await api.delete(`/tasks/external-calendars/${id}`);
      toast.success('Calendrier supprime');
      load();
      onSynced();
    } catch { toast.error('Erreur'); }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/tasks/external-calendars/sync');
      toast.success(res.data.message);
      onSynced();
    } catch { toast.error('Erreur de synchronisation'); }
    setSyncing(false);
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api.post('/telegram/calendar-digest');
      toast.success(res.data.message);
    } catch { toast.error('Erreur envoi Telegram'); }
    setSendingDigest(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="lm-card w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()} data-testid="external-calendars-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-blue-400" />
            <h2 className="text-sm font-medium text-[rgb(var(--lm-text))]">Calendriers externes</h2>
          </div>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={16} /></button>
        </div>

        {/* Add new */}
        <div className="space-y-2 p-3 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Ajouter un calendrier Apple iCloud</p>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nom (ex: Perso, Boulot...)"
            className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs h-8" data-testid="cal-name-input" />
          <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="webcal://p123-caldav.icloud.com/..."
            className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs font-mono h-8" data-testid="cal-url-input" />
          <Button onClick={addCal} disabled={adding} size="sm" className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs h-8" data-testid="add-cal-btn">
            {adding ? <Loader2 size={12} className="animate-spin mr-1" /> : <Plus size={12} className="mr-1" />}
            Ajouter
          </Button>
        </div>

        {/* Instructions */}
        <div className="p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/15">
          <p className="text-[10px] text-blue-400 font-bold mb-1">Comment obtenir l'URL iCal sur iPhone :</p>
          <div className="text-[9px] text-[rgb(var(--lm-text-muted))] space-y-0.5">
            <p>1. Allez sur <b>icloud.com/calendar</b> (Safari)</p>
            <p>2. Cliquez sur l'icone partage a cote du calendrier</p>
            <p>3. Cochez "Calendrier public"</p>
            <p>4. Copiez le lien <b>webcal://...</b></p>
            <p>5. Collez-le ici</p>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-4"><Loader2 size={16} className="animate-spin text-[rgb(var(--lm-text-muted))] mx-auto" /></div>
        ) : calendars.length === 0 ? (
          <p className="text-xs text-[rgb(var(--lm-text-muted))] text-center py-4">Aucun calendrier connecte</p>
        ) : (
          <div className="space-y-1.5">
            {calendars.map(c => (
              <div key={c.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]" data-testid={`ext-cal-${c.id}`}>
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color || '#3B82F6' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[rgb(var(--lm-text))] truncate">{c.name}</p>
                  <p className="text-[9px] text-[rgb(var(--lm-text-muted))] truncate">{c.last_synced ? `Sync: ${new Date(c.last_synced).toLocaleString('fr-FR')}` : 'Jamais synchronise'}</p>
                </div>
                <button onClick={() => removeCal(c.id)} className="p-1 rounded hover:bg-red-500/10 text-red-400" data-testid={`remove-cal-${c.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={syncAll} disabled={syncing} variant="outline" size="sm" className="flex-1 text-xs h-8 border-[rgb(var(--lm-border))]" data-testid="sync-calendars-btn">
            {syncing ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
            Synchroniser
          </Button>
          <Button onClick={sendDigest} disabled={sendingDigest} variant="outline" size="sm" className="flex-1 text-xs h-8 border-[rgb(var(--lm-border))]" data-testid="send-digest-btn">
            {sendingDigest ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
            Telegram
          </Button>
        </div>
      </div>
    </div>
  );
}
